import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import { SceneObject, Evidence, Movement } from '../types';
import { getPersonColor, isSceneObject, isEvidence } from '../utils/helpers';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapViewProps {
  sceneObjects: SceneObject[];
  evidenceList: Evidence[];
  movements: Movement[];
  currentCaseId: string;
  mode: 'map' | 'local';
  attributeLock: any;
  attributeFilters: any;
  startTimeRange: number;
  endTimeRange: number;
  getTimestampFromSlider: (value: number) => number;
  getRelevantLocations: (attribute: string, value: string) => string[];
  onObjectClick: (obj: SceneObject | Evidence) => void;
  showMovementLines: boolean;
}

const MapView: React.FC<MapViewProps> = ({
  sceneObjects,
  evidenceList,
  movements,
  currentCaseId,
  mode,
  attributeLock,
  attributeFilters,
  startTimeRange,
  endTimeRange,
  getTimestampFromSlider,
  getRelevantLocations,
  onObjectClick,
  showMovementLines
}) => {

  // Filter functions
  const filteredByTime = (obj: SceneObject | Evidence): boolean => {
    const objTime = new Date(obj.time).getTime();
    const startTime = getTimestampFromSlider(startTimeRange);
    const endTime = getTimestampFromSlider(endTimeRange);
    return objTime >= startTime && objTime <= endTime;
  };

  const filteredByCase = (obj: SceneObject | Evidence): boolean => {
    return obj.caseId === currentCaseId;
  };

  const filteredByAttributes = (obj: SceneObject | Evidence): boolean => {
    if ('people' in obj) {
      const hasFilteredPeople = obj.people.some(person => attributeFilters.people[person] === false);
      if (hasFilteredPeople) return false;
    }
    
    const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
    if (attributeFilters.location[locationName] === false) return false;
    
    const dateString = new Date(obj.time).toDateString();
    if (attributeFilters.time[dateString] === false) return false;
    
    return true;
  };

  const filteredByLock = (obj: SceneObject | Evidence): boolean => {
    if (!attributeLock.attribute || !attributeLock.value) return true;
    
    if (attributeLock.attribute === 'people') {
      if ('people' in obj) {
        return obj.people.includes(attributeLock.value);
      }
      return false;
    } else if (attributeLock.attribute === 'location') {
      const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
      // For location locks, show items that match the exact location OR are local locations within that global location
      if (locationName === attributeLock.value) return true;
      
      // If the locked location is a global location, also show local locations within it
      if (obj.location.mode === 'local' && obj.location.local?.parentLocation === attributeLock.value) {
        return true;
      }
      
      return false;
    } else if (attributeLock.attribute === 'time') {
      const objDateTime = new Date(obj.time).toISOString();
      return objDateTime === attributeLock.value;
    }
    
    return true;
  };

  // Filter objects for map view (show all but mark irrelevant ones as greyed out)
  const filteredSceneObjects = sceneObjects.filter(obj => 
    obj.location.mode === 'global' &&
    filteredByTime(obj) &&
    filteredByCase(obj)
  );

  const filteredEvidence = evidenceList.filter(obj => 
    obj.location.mode === 'global' &&
    filteredByTime(obj) &&
    filteredByCase(obj)
  );

  // Filter movements for map view
  const filteredMovements = movements.filter(movement => {
    const isFromGlobal = movement.fromLocation.mode === 'global';
    const isToGlobal = movement.toLocation.mode === 'global';
    const movementTime = new Date(movement.time).getTime();
    const startTime = getTimestampFromSlider(startTimeRange);
    const endTime = getTimestampFromSlider(endTimeRange);
    
    return (isFromGlobal || isToGlobal) && movementTime >= startTime && movementTime <= endTime;
  });

  // Get all unique people for color assignment
  const allPeople = Array.from(new Set(
    sceneObjects.flatMap(obj => 'people' in obj ? obj.people : [])
  ));

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <MapContainer
        center={[1.3521, 103.8198]} // Singapore center
        zoom={11}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        boxZoom={true}
        keyboard={true}
        dragging={true}
        touchZoom={true}
      >
        <TileLayer
          url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://stamen.com/">Stamen Design</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
          maxZoom={20}
          minZoom={0}
        />

        {/* Render scene objects */}
        {filteredSceneObjects.map(obj => {
          // Check if this object is relevant based on attribute lock
          const isRelevant = filteredByLock(obj);

          // Check if this object is available based on attribute filters
          const isAvailable = filteredByAttributes(obj);

          const shouldGreyOut = !isRelevant || !isAvailable;

          return (
            <Marker
              key={obj.id}
              position={[obj.location.global!.lat, obj.location.global!.lng]}
              eventHandlers={{
                click: shouldGreyOut ? () => {} : () => onObjectClick(obj)
              }}
              icon={new L.Icon({
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
                className: shouldGreyOut ? 'greyed-out-marker' : ''
              })}
            />
          );
        })}

        {/* Render evidence */}
        {filteredEvidence.map(obj => {
          // Check if this object is relevant based on attribute lock
          const isRelevant = filteredByLock(obj);

          // Check if this object is available based on attribute filters
          const isAvailable = filteredByAttributes(obj);

          const shouldGreyOut = !isRelevant || !isAvailable;

          return (
            <Marker
              key={obj.id}
              position={[obj.location.global!.lat, obj.location.global!.lng]}
              eventHandlers={{
                click: shouldGreyOut ? () => {} : () => onObjectClick(obj)
              }}
              icon={new L.Icon({
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
                className: shouldGreyOut ? 'greyed-out-marker' : ''
              })}
            />
          );
        })}

        {/* Render movement lines */}
        {showMovementLines && filteredMovements.map(movement => {
          const personColor = getPersonColor(movement.person, allPeople);
          
          // Get coordinates for movement line
          let fromCoords: [number, number] | null = null;
          let toCoords: [number, number] | null = null;

          if (movement.fromLocation.mode === 'global' && movement.fromLocation.global) {
            fromCoords = [movement.fromLocation.global.lat, movement.fromLocation.global.lng];
          }
          if (movement.toLocation.mode === 'global' && movement.toLocation.global) {
            toCoords = [movement.toLocation.global.lat, movement.toLocation.global.lng];
          }

          if (fromCoords && toCoords) {
            return (
              <Polyline
                key={movement.id}
                positions={[fromCoords, toCoords]}
                color={personColor}
                weight={3}
                opacity={0.7}
              />
            );
          }
          return null;
        })}
      </MapContainer>
    </div>
  );
};

export default MapView;
